import axios from 'axios';
import moment from 'moment';
import errorHandler from './mixins/errorHandler';
export default class RestResource {
    static baseUrl = "";
    constructor() {
        this.baseUrl = process.env.VUE_APP_API_BASEURL;
    }
    //GET BASE URL
    getBaseUrl() {
        return this.baseUrl;
    }

    //HOW REST-RESOURCE WORKS
    //  -Call your methods from else where in the app.
    //  -No need to wrap this call in anything (await or then catch)

    //  -The methods is called and uses the generic which returns a promise containing response.
    //  -In the method the then is used to exact the object needed from the response and return it.
    //  -Errors are caught and passed to errorHandler from the generic, and then a default object can be returned to the origin
    //   allowing for checks like if(!returned) doSomething();

    //Benefit of this is that the data returned for each call can be handled inside the rest resource instead of the application.

    //GENERIC METHODS
    genericGet(url, customErrom, params) {
        return new Promise((resolve, reject) => {
            axios
                .get(this.baseUrl + url, { params: params })
                .then((response) => {
                    resolve(response);
                }).catch((error) => {
                    reject(errorHandler.methods.handleErrorResponse(error, customErrom));
                })
        })
    }

    genericPost(url, customErrom, data) {
        return new Promise((resolve, reject) => {
            axios
                .post(this.baseUrl + url, data)
                .then((response) => {
                    resolve(response);
                }).catch((error) => {
                    reject(errorHandler.methods.handleErrorResponse(error, customErrom));
                })
        })
    }

    //AUTH
    getCurrentAccount() {
        return this.genericGet("auth/currentaccount/", "Couldn't retrieve account").then(res => {
            console.log({ res })
            return res.data;
        }).catch(function () {
            return null;
        });
    }

    postLogin(email, password) {
        return this.genericPost("auth/login", "Invalid login credentials", { email: email, password: password }).then(function () { return true; }).catch(function () { return false; });
    }

    getLogout() {
        return this.genericPost("auth/logout/", "Couldn't logout at this time").then(function () {
            return true;
        }).catch(function () {
            return false;
        })
    }


    //PARTNER - Transactions
    getTransactions(startDate, endDate) {
        return this.genericGet("partner/transactions/", "Couldn't retrieve transactions", { start: moment(startDate).format('YYYY-MM-DD'), end: moment(endDate).format('YYYY-MM-DD') })
            .then(response => {
                return response.data;
            })
            .catch(function () {
                return null;
            })
    }


    //PARTNER - Account

    //SYNC 
    getBalance() {
        return this.genericGet("partner/balance/", "Couldn't retrieve balance").then(response => { return response.data }).catch(function () { return { balance: null, pendingPayments: [] }; });
    }

    getBankDetails() {
        return this.genericGet("partner/paymentdetails/", "Couldn't retrieve bank details").then(response => { if (response.data.accountNumber != null) return response.data; else { const defaultAcc = { accountNumber: 'None', sortCode: 'None', iban: 'None' }; return defaultAcc; } })
            .catch(function () { const defaultAcc = { accountNumber: 'None', sortCode: 'None', iban: 'None' }; return defaultAcc; })
    }

    getAccountAddress() {
        return this.genericGet("auth/currentaccountdetails/", "Couldn't account information").then(response => { return response.data; }).catch(function () { return { id: -1 }; })
    }

    getPartnerSites() {
        return this.genericGet("partner/sites", "Couldn't get available sites at this time").then(response => { return response.data; }).catch(function () { return null })
    }

    //PARTNER ACTIONS
    getPartnerCheckin(checkInObj) {
        return this.genericPost("partner/checkin", "Couldn't check-in at this time", checkInObj).then(response => { return response.status }).catch(function () { return 400 })
    }

    //PARTNER - Updating Account
    async getRecaptchaSiteKey() {
        const response = await this.genericPost("partner/retrieveRecaptchaSiteKey", "Couldn't get site key at this time");
        return response.data;
    }

    postPasswordReset(email, recaptchaResponse) {
        return new Promise((resolve, reject) => {
            axios
                .post(this.baseUrl + "partner/passwordreset"
                    , { email: email, recaptchaResponse: recaptchaResponse })
                .then(() => {
                    resolve(true);
                }).catch((error) => {
                    let customErrom = "Couldn't reset password at this time";
                    reject(errorHandler.methods.handleErrorResponse(error, customErrom));
                });
        })
    }

    updateAccount(account) {
        return this.genericPost("partner/updateAccount", "Couldn't update account at this time", account).then(response => { return response.status }).catch(function () { return 400 });
    }

    updateAccountDetails(accountDetails) {
        return this.genericPost("partner/updateAccountDetails", "Couldn't update account details at this time", accountDetails).then(response => { return response.status }).catch(function () { return 400 });
    }

    postUpdateBankDetails(bankDetails) {
        return this.genericPost("partner/paymentdetails", "Couldn't update bank details at this time", bankDetails).then(response => { return response.status }).catch(function () { return 400 });
    }

    postUpdatePassword(pass1, pass2, guid) {
        return this.genericPost("partner/updatePassword/" + guid, "Couldn't update password at this time", { password1: pass1, password2: pass2 }).then(function () { return true }).catch(function () { return false });
    }

    setProfilePicture(name) {
        return this.genericPost("partner/setProfilePicture/" + name, "Couldn't set profile picture at this time").then(response => { return response.status }).catch(function () { return 400 });
    }


    //PUSH NOTIFICATIONS
    subscribeForPush(sub) {
        return this.genericPost("push/subscribe", "Couldn't subscribe to push notifications at this time", sub).then(response => { return response.data.key; }).catch(function () { return null })
    }

    testPushSub() {
        return this.genericGet("push/testnotification", "Couldn't test push notifications at this time").then(response => { return response.data.key; }).catch(function () { return null });
    }

    getPushPublicKey() {
        return this.genericGet("push/publickey", "Couldn't get push notifications at this time").then(response => { return response.data.key; }).catch(function () { return null });
    }

    //Notifications
    getUnreadMessages() {
        return this.genericGet("partner/messages", "Couldn't retrieve inbox at this time").then(response => { return response.data }).catch(function () { return null });
    }
    getAllMessages() {
        return this.genericGet("partner/allmessages", "Couldn't retrieve inbox at this time").then(response => { return response.data }).catch(function () { return null });
    }

    postReadMessage(messageid) {
        return this.genericPost("partner/readMessage/" + messageid, "Couldn't update message to read at this time").then(response => { return response.status }).catch(err => { return err.status });
    }

    postToggleStarMessage(messageid) {
        return this.genericPost("partner/starMessage/" + messageid, "Couldn't star the message at this time").then(response => { return response.status }).catch(err => { return err.status });
    }

    //S3 get album
    getAccountAlbum() {
        return this.genericGet("partner/uploads", "Couldn't access images at this time").then(response => { return response.data }).catch(function () { return null });
    }
    //S3 Bucket Uploads
    uploadImageToS3(imageFile) {
        return new Promise((resolve, reject) => {
            axios
                .get(this.baseUrl + "partner/uploadurl?filename=" + imageFile.name)
                .then((response) => {
                    var reader = new FileReader();
                    var xhr = new XMLHttpRequest();

                    xhr.open("PUT", response.data.url);
                    Object.keys(response.data.headers).forEach(key => {
                        xhr.setRequestHeader(key, response.data.headers[key]);
                    });
                    reader.onload = function (evt) {
                        xhr.send(evt.target.result);
                    };
                    reader.readAsArrayBuffer(imageFile);
                    resolve(response);
                    //This is because the problem is S3 is looking for a specific Content-Type (binary/octet-stream)



                }).catch((error) => {
                    let customErrom = "Couldn't upload image at this time";
                    reject(errorHandler.methods.handleErrorResponse(error, customErrom));
                });
        });
    }

    //S3 Bucket Uploads
    uploadProofImageToS3(imageFile, proofName) {
        return new Promise((resolve, reject) => {
            axios
                .get(this.baseUrl + "partner/uploadproofurl?filename=" + imageFile.name + "&proofname=" + proofName)
                .then((response) => {
                    var reader = new FileReader();
                    var xhr = new XMLHttpRequest();

                    xhr.open("PUT", response.data.url);
                    Object.keys(response.data.headers).forEach(key => {
                        xhr.setRequestHeader(key, response.data.headers[key]);
                    });
                    reader.onload = function (evt) {
                        xhr.send(evt.target.result);
                    };
                    reader.readAsArrayBuffer(imageFile);
                    resolve(response);
                    //This is because the problem is S3 is looking for a specific Content-Type (binary/octet-stream)



                }).catch((error) => {
                    let customErrom = "Couldn't upload image at this time";
                    reject(errorHandler.methods.handleErrorResponse(error, customErrom));
                });
        });
    }

    getAllPaymentDetails() {
        return this.genericGet("partner/allpaymentdetails", "Couldn't access payment details at this time").then(response => { return response.data }).catch(function () { return null });
    }

    updatePaymentDetails(pd) {
        return this.genericPost("partner/changedefaultpayment", "Couldn't update payment details at this time", pd).then(response => { return response.data }).catch(err => { return err });
    }

    deletePaymentDetails(pdId) {

        return axios
            .delete(this.baseUrl + "partner/paymentdetails?paymentId=" + pdId)
            .then((response) => {
                return response;
            }).catch((error) => {
                return error;
            })

    }

    getOnboardingProgress() {
        return this.genericGet("partner/getOnboardingProgress", "Couldn't access payment details at this time")
            .then(response => {
                return response.data
            })
            .catch(function () {
                return null
            });
    }

    submitForm(formData) {
        return this.genericPost("partner/submitOnboardingForm", "Couldn't send form at this time", formData)
            .then(response => { return response.data })
            .catch(err => { return err });
    }

    sendForReview() {
        return this.genericGet("partner/sendForReview", "Couldn't send for reviewing at this time")
            .then(response => {
                return response.data
            })
            .catch(function () {
                return null
            });
    }
}
